import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Permission, PermissionType } from './permission';
import { IMap } from '../helpers';
import { IRouteAuthConfiguration, IEntityRouteAuthConfiguration } from './permission';
import { Configuration } from '../configuration';
import * as _ from 'underscore';
import { BehaviorSubject } from 'rxjs';
import { appInsights } from '../app.module';
const jwtDecode = require('jwt-decode');
//const jwtEncode = require('jwt-encode');

@Injectable()
export class LoggedInUserService {
    private currentUserProfileId: number;
    private cachedUser: LoggedInUser;

    constructor(private configuration: Configuration,
        private router: Router) {
        this.currentUserProfileId = 0;
    }

    getCurrentUser(): LoggedInUser {
        if (this.cachedUser) {
            this.refreshUserClaims();
            return this.cachedUser;
        }

        const token = localStorage.getItem(this.configuration.auth.jwtAuthTokenStorageId);
        if (token) {
            const user = new LoggedInUser(token);

            if (this.cachedUser == null || user.userId !== this.cachedUser.userId) {
                this.cachedUser = user;
            }

            return user;
        }

        return null;
    }

    refreshUserClaims() {
        if (this.cachedUser) {
            this.cachedUser.createUserFromToken(localStorage.getItem(this.configuration.auth.jwtAuthTokenStorageId));
        }
    }

    logout() {
        this.cachedUser = null;
    }
}

export interface IUserProfileEntityClaim {
    name: string;
    read: boolean;
    add: boolean;
    edit: boolean;
    delete: boolean;
}

export interface IUserProfileClaim {
    id: number;
    name: string;
    entities: IUserProfileEntityClaim[];
    adHocPermissions: string[];
}

export enum BidManagementStatus {
    Current,
    Expired,
    None
}

export class LoggedInUser {
    token: string;
    userId: number;
    email: string;
    fullName: string;
    profile: IUserProfileClaim;
    entityPermissions: IMap<Permission>;
    currentProfile: BehaviorSubject<IUserProfileClaim>;
    supplierId: number;
    teamLeaderBidManagementStatus: BidManagementStatus;
    role: string;
    subscriptionExpired: boolean;

    constructor(jwtToken: string) {
        this.createUserFromToken(jwtToken);
    }

    createUserFromToken(jwtToken: string) {
        this.token = jwtToken;

        const decodedToken = jwtDecode(jwtToken);

        this.userId = parseInt(decodedToken.sub);
        this.email = decodedToken.name;
        this.fullName = decodedToken.fullName;
        this.supplierId = parseInt(decodedToken.supplierId);
        this.role = decodedToken.role;
        this.subscriptionExpired = decodedToken.subscriptionExpired === 'True';

        if (decodedToken.teamLeadBidManager) {
            switch (decodedToken.teamLeadBidManager) {
                case 'current':
                    {
                        this.teamLeaderBidManagementStatus = BidManagementStatus.Current;
                        break;
                    }
                case 'expired':
                    {
                        this.teamLeaderBidManagementStatus = BidManagementStatus.Expired;
                        break;
                    }
                default:
                    {
                        this.teamLeaderBidManagementStatus = BidManagementStatus.None;
                    }
            }
        }

        this.profile = decodedToken.profiles as IUserProfileClaim;

        this.entityPermissions = {};
        this.setEntityPermissions();

        if (!this.currentProfile) {
            this.currentProfile = new BehaviorSubject(this.profile);
        } else {
            this.currentProfile.next(this.profile);
        }

        appInsights.setAuthenticatedUserContext(this.email);
    }

    setEntityPermissions() {
        _.each(this.profile.entities,
            entity => {
                this.entityPermissions[entity.name] = new Permission(entity);
            });
    }

    canEntity(entity: string, type: PermissionType) {
        if (!this.entityPermissions) {
            return false;
        }

        const permission = this.entityPermissions[entity];

        if (!permission) {
            return false;
        }

        return permission.can(type);
    }

    canAdHoc(permission: string) {
        if (!this.profile) {
            return false;
        }
        return _.contains(this.profile.adHocPermissions, permission);
    }

    canActivate(routeAuthConfig: IRouteAuthConfiguration) {
        //If there is no config they have not tried to lock it down, so allow access
        if (!routeAuthConfig) {
            return true;
        }

        let adHocPermissionRequirements: string[];

        const requiresAdHoc = routeAuthConfig.requiresAdHocPermissions;
        if (requiresAdHoc instanceof Array) {
            adHocPermissionRequirements = requiresAdHoc as string[];
        } else if (typeof requiresAdHoc === 'undefined') {
            adHocPermissionRequirements = [];
        } else {
            adHocPermissionRequirements = [requiresAdHoc as string];
        }

        const fulfillsAdHocRequirements = adHocPermissionRequirements.every(permission => {
            var components = permission.split('|');

            return components.some(c => this.canAdHoc(c));
        });

        if (!fulfillsAdHocRequirements) {
            return false;
        }

        let entityPermissionRequirments: IEntityRouteAuthConfiguration[];

        const requiresEntities = routeAuthConfig.requiresEntityPermissions;
        if (requiresEntities instanceof Array) {
            entityPermissionRequirments = requiresEntities as IEntityRouteAuthConfiguration[];
        } else if (typeof requiresEntities === 'undefined') {
            entityPermissionRequirments = [];
        } else {
            entityPermissionRequirments = [requiresEntities as IEntityRouteAuthConfiguration];
        }
        //Check evey requirement is valid for this user
        return entityPermissionRequirments.every(requirement => {
            let permissionTypes: PermissionType[];

            const permission = requirement.permission;
            if (permission instanceof Array) {
                permissionTypes = permission as PermissionType[];
            } else {
                permissionTypes = [permission as PermissionType];
            }

            //Check the required types are present
            return permissionTypes.every(permissionType => this.canEntity(requirement.entity, permissionType));
        });
    }
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Client/app/auth/logged.in.user.ts